/**
 * Safe handling of the `?data=` payload that /SearchListing and /ConfirmBooking
 * read straight out of the URL.
 *
 * WHY THIS EXISTS
 * ---------------
 * Both pages used to do
 *
 *     JSON.parse(decodeURIComponent(this.$route.query.data))
 *
 * with no try/catch, and then render fields of the result. The payload is
 * entirely caller-supplied, so anyone could craft a chooseapp.in URL that
 * displayed content of their choosing on a real page of ours, over a valid
 * certificate. Vue escapes `{{ }}` interpolations, so this was never XSS — but
 * social engineering only needs plain text. Google flagged chooseapp.in under
 * "Deceptive pages" on 2026-07-24, and the sample URL it reported was exactly
 * /SearchListing?data=<blob>, where the blob decoded to our own payload shape
 * with fuzzed values (month "50", junk escapes like %7K). Someone was probing
 * the parameter to see what would render.
 *
 * The rule enforced here: nothing from the URL reaches the DOM unless it looks
 * like something our own search widget produced. Fields that have a fixed shape
 * (dates, times, coordinates, image URLs) are validated against that shape and
 * dropped when they do not match. Free-text fields (addresses) are length- and
 * character-capped, which is the most that can be done for a field whose real
 * values are arbitrary text.
 */

// tripType values the search widget emits — see searchwidget.vue `unifiedForm`
// ("1" outstation, "2" round trip, "3" multi-city, "4" airport, "5" rental).
const TRIP_TYPES = ["1", "2", "3", "4", "5"];

// A whole encoded payload above this size is not something our widget produced.
const MAX_PAYLOAD_LENGTH = 8000;

// Characters that legitimately occur in the addresses the place picker returns:
// Latin letters (incl. accents), Devanagari, digits, spaces and ordinary
// punctuation. Deliberately excludes < > " newlines and anything else that
// belongs to markup rather than to a street address.
// Ranges are written as \u escapes rather than literals: U+0900 is a combining
// mark, and as a literal it merges with the preceding character in the source
// (which eslint's no-misleading-character-class rightly rejects).
const SAFE_TEXT_RE =
  // The Devanagari block contains combining marks, which trips
  // no-misleading-character-class. That rule guards against treating a
  // multi-code-point grapheme as separate alternatives; here the class is only
  // ever used to assert that a string contains nothing outside these ranges, so
  // per-code-point matching is exactly what is wanted.
  // eslint-disable-next-line no-misleading-character-class
  new RegExp(
    "^[A-Za-z0-9" +
      "\\u00C0-\\u024F" + // Latin-1 Supplement + Latin Extended-A/B (accents)
      "\\u0900-\\u097F" + // Devanagari
      " .,'()\\-/&#:;]*$"
  );

// Image URLs may only point at our own asset hosts. An arbitrary `:src` is the
// strongest deceptive-content vector on these pages: a remote or data: image can
// render a complete fake notice that Vue's text escaping does nothing about.
const ALLOWED_IMAGE_HOSTS = [
  "vultrobjects.com",
  "choosecabs.com",
  "chooseapp.in",
];

/**
 * Decode and parse the raw `?data=` value.
 * Returns a plain object, or null if the value is absent, oversized, not
 * decodable (fuzzed percent-escapes throw here), not JSON, or not an object.
 */
export function parseUrlPayload(raw) {
  if (typeof raw !== "string" || raw.length === 0) return null;
  if (raw.length > MAX_PAYLOAD_LENGTH) return null;

  let parsed;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    // Malformed percent-encoding or malformed JSON. Either way it did not come
    // from us.
    return null;
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  return parsed;
}

/** Free text, collapsed, character-checked and length-capped. "" when unusable. */
export function safeText(value, maxLength = 40) {
  if (typeof value !== "string") return "";
  const collapsed = value.replace(/\s+/g, " ").trim();
  if (!collapsed) return "";
  if (!SAFE_TEXT_RE.test(collapsed)) return "";
  // Addresses never contain links; a link is a strong sign of an injected message.
  if (/(https?:|www\.)/i.test(collapsed)) return "";
  // Nor do they contain phone numbers, which is what a "call this number" scam
  // needs. Seven is the threshold because a six-digit run is a valid Indian PIN
  // code ("Vashi, Navi Mumbai 400703"), while mobile numbers are ten digits and
  // landlines eight or more.
  if (/\d{7,}/.test(collapsed.replace(/[\s.\-/]/g, ""))) return "";
  return collapsed.length > maxLength
    ? collapsed.slice(0, maxLength) + "..."
    : collapsed;
}

/**
 * A real calendar date in YYYY-MM-DD form, else "".
 * This is what rejects Google's sample payload, whose startDate was "2038-50-29".
 */
export function safeDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const roundTrips =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  return roundTrips ? value : "";
}

/** A wall-clock time in HH:MM or HH:MM:SS form, else "". */
export function safeTime(value) {
  if (typeof value !== "string") return "";
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return "";
  const [hours, minutes, seconds] = [match[1], match[2], match[3] ?? "00"].map(Number);
  if (hours > 23 || minutes > 59 || seconds > 59) return "";
  return value;
}

/** A finite number within `limit` degrees of zero, else null. */
function safeCoordinate(value, limit) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number) || Math.abs(number) > limit) return null;
  return number;
}

export const safeLatitude = (value) => safeCoordinate(value, 90);
export const safeLongitude = (value) => safeCoordinate(value, 180);

/**
 * An image URL on one of our own hosts, or a relative path within the bundle.
 * Anything else (foreign host, data:, javascript:, protocol-relative) yields the
 * caller's fallback.
 */
export function safeImageUrl(value, fallback = "") {
  if (typeof value !== "string" || !value) return fallback;
  if (value.startsWith("//")) return fallback;

  // Relative path inside our own bundle.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(value)) return value;

  let url;
  try {
    url = new URL(value);
  } catch {
    return fallback;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return fallback;

  const host = url.hostname.toLowerCase();
  const allowed = ALLOWED_IMAGE_HOSTS.some(
    (candidate) => host === candidate || host.endsWith("." + candidate)
  );
  return allowed ? value : fallback;
}

/**
 * Structural check for the search payload before it is rendered or sent to the
 * API. Returns false for anything our widget would not have produced.
 * Free-text fields are not judged here — `safeText` handles those at render time.
 */
export function isPlausibleSearchPayload(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (!TRIP_TYPES.includes(String(payload.tripType))) return false;
  if (!safeDate(String(payload.startDate ?? ""))) return false;
  if (!safeTime(String(payload.startTime ?? ""))) return false;

  // Multi-city (tripType 3) carries its stops in `cities` and may leave the
  // top-level source/destination blank, so coordinates are only required to be
  // well-formed when present, never to be non-null.
  const coordinateFields = [
    ["sourceLat", safeLatitude],
    ["destinationLat", safeLatitude],
    ["sourceLong", safeLongitude],
    ["destinationLong", safeLongitude],
  ];
  for (const [field, validate] of coordinateFields) {
    const value = payload[field];
    if (value !== null && value !== undefined && value !== "" && validate(value) === null) {
      return false;
    }
  }

  if (payload.cities !== undefined && !Array.isArray(payload.cities)) return false;
  return true;
}
